// Tabs.jsx

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// CSS
import './Tabs.css';

// Components
import RichSelect from 'components/richselect/RichSelect';
import Tab from './tab/Tab';

/**
 * Tabs
 *
 * Container for tab components. When tabs do not fit the available width,
 * renders a RichSelect instead of a clipped tab row with a More overflow menu.
 *
 * @param {object[]} tabs - Array of tab objects with {key, label, icon?, iconKey?, description?, onClick, rightContent?}
 * @param {string} selectedKey - The key of the currently selected tab
 * @param {boolean} showLabels - Whether to show tab labels (if false, only icons are shown)
 * @param {Number} tabWidth - (Optional) Fixed width for each tab
 *
 * @returns {ReactNode} - Tabs.
 */
const Tabs = React.memo(({ tabs, selectedKey, showLabels, tabWidth }) => {

  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const containerRef = useRef(null);
  const tabRefs = useRef(new Map());

  const [useRichSelect, setUseRichSelect] = useState(false);
  const layoutModeRef = useRef(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  const richSelectOptions = useMemo(() => (
    (Array.isArray(tabs) ? tabs : []).map((tab) => ({
      value: tab.key,
      label: tab.label,
      iconKey: tab.iconKey || '',
      note: tab.description || tab.note || '',
    }))
  ), [tabs]);

  const updateLayoutMode = useCallback(() => {
    if (!wrapperRef.current || !measureRef.current) return;

    const availableWidth = wrapperRef.current.clientWidth;
    const requiredWidth = measureRef.current.scrollWidth;
    const overflowThreshold = 1;
    const collapseThreshold = 24;

    if (requiredWidth > availableWidth + overflowThreshold) {
      layoutModeRef.current = true;
    } else if (availableWidth > requiredWidth + collapseThreshold) {
      layoutModeRef.current = false;
    }

    setUseRichSelect(layoutModeRef.current);
  }, []);

  const updateIndicator = useCallback(() => {
    if (useRichSelect || !containerRef.current || !selectedKey) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const activeTab = tabRefs.current.get(selectedKey);
    if (!activeTab) {
      setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const indicatorWidth = tabRect.width * 0.8;
    const centeredLeft = (tabRect.left - containerRect.left) + ((tabRect.width - indicatorWidth) / 2);

    setIndicatorStyle({
      left: centeredLeft,
      width: indicatorWidth,
      opacity: 1,
    });
  }, [selectedKey, useRichSelect]);

  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      updateLayoutMode();
      updateIndicator();
    });
    return () => cancelAnimationFrame(raf);
  }, [tabs, selectedKey, showLabels, tabWidth, updateIndicator, updateLayoutMode]);

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updateLayoutMode();
      updateIndicator();
    });

    resizeObserver.observe(wrapperRef.current);
    if (measureRef.current) resizeObserver.observe(measureRef.current);
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [updateIndicator, updateLayoutMode]);

  useEffect(() => {
    window.addEventListener('resize', updateLayoutMode);
    window.addEventListener('resize', updateIndicator);
    return () => {
      window.removeEventListener('resize', updateLayoutMode);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [updateIndicator, updateLayoutMode]);

  const handleRichSelectChange = useCallback((_nextValue, option) => {
    const nextKey = option?.value;
    const matchedTab = (Array.isArray(tabs) ? tabs : []).find((tab) => tab.key === nextKey);
    matchedTab?.onClick?.();
  }, [tabs]);

  return (
    <div
      ref={wrapperRef}
      className={`tabs-wrapper${useRichSelect ? ' tabs-wrapper--rich-select' : ''}`}
    >
      <div
        ref={measureRef}
        className="tabs-measure-container"
        aria-hidden="true"
      >
        {(Array.isArray(tabs) ? tabs : []).map((tab) => (
          <Tab
            key={`measure-${tab.key}`}
            tabKey={tab.key}
            label={tab.label}
            icon={tab.icon}
            iconKey={tab.iconKey}
            isActive={false}
            rightContent={tab.rightContent}
            width={tabWidth}
            showLabel={showLabels}
            onClick={() => {}}
          />
        ))}
      </div>

      {useRichSelect ? (
        <RichSelect
          className="tabs-rich-select"
          value={selectedKey || ''}
          onValueChange={handleRichSelectChange}
          options={richSelectOptions}
          placeholder="Select section..."
          hybrid={true}
          style={{ 
            background: "transparent",
            fontSize: "16px",
            fontWeight: 500,
            borderWidth: 0,
            borderRadius: 0,
            boxShadow: "none",
            width: '100%' 
          }}
        />
      ) : (
        <div ref={containerRef} className="tabs-container">
          {(Array.isArray(tabs) ? tabs : []).map((tab) => (
            <Tab
              key={tab.key}
              tabKey={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconKey={tab.iconKey}
              isActive={tab.key === selectedKey}
              rightContent={tab.rightContent}
              width={tabWidth}
              showLabel={showLabels}
              onClick={tab.onClick}
              tabRef={(node) => {
                if (!node) {
                  tabRefs.current.delete(tab.key);
                  return;
                }
                tabRefs.current.set(tab.key, node);
              }}
            />
          ))}

          <span
            className="tabs-active-indicator"
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
              opacity: indicatorStyle.opacity,
            }}
          />
        </div>
      )}
    </div>
  );
});

Tabs.displayName = 'Tabs';

export default Tabs;
